import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  ListItem,
  ListItemText,
  Paper,
  Button
} from '@mui/material';
import axios from 'axios';
import { FixedSizeList as List } from 'react-window';
import { API_BASE_URL } from 'src/apiConfig';
import StudentDetails from './index';
import { useParams } from 'react-router-dom';
import { cyrillicToLatin } from 'src/helper/helperFunction';
import { useTranslation } from 'react-i18next';
import AddStudentDialog from 'src/components/dialogs/student/addStudentDialog';

const FinalClassStudents = () => {
  const { classNumber } = useParams();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openStudentDialog, setOpenStudentDialog] = useState(false);
  const [openSchoolDialog, setOpenSchoolDialog] = useState(false);
  const { t } = useTranslation();

  const handleDialogToggle = (type, status, data) => {
    if (type === 'student') {
      setOpenStudentDialog(status); // Ponovo otvori dijalog
      setSelectedStudent(null); // Resetuj prethodni selektovani učenik
      setSelectedStudent(data || null); // Postavi novi selektovani učenik
    } else if (type === 'school') {
      setOpenSchoolDialog(status);
      setSelectedSchool(data || null);
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/finalReviewAll.php?action=getStudentsByClass&class=${classNumber}`
      );
      const fetchedStudents = response.data.students || [];

      setStudents(fetchedStudents);

      if (selectedStudentId) {
        const selected = fetchedStudents.find(
          (student) => student.id === selectedStudentId
        );
        if (selected) {
          setSelectedStudent(selected);
          setSchool(selected.schoolid);
        }
      } else if (fetchedStudents.length > 0) {
        setSelectedStudentId(fetchedStudents[0].id); // Ako nema selekcije, postavi prvog učenika
        setSelectedStudent(fetchedStudents[0]);
        setSchool(fetchedStudents[0].schoolid);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [classNumber]);

  useEffect(() => {
    const normalizedSearchTerm = cyrillicToLatin(searchTerm.toLowerCase());
    if (normalizedSearchTerm === '') {
      setFilteredStudents(students);
    } else {
      const filtered = students.filter((student) => {
        const normalizedStudentName = cyrillicToLatin(
          student.name.toLowerCase()
        );
        return normalizedStudentName.includes(normalizedSearchTerm);
      });
      setFilteredStudents(filtered);
    }
  }, [searchTerm, students]);

  const handleStudentClick = (student) => {
    // Privremeno resetuje selektovanog učenika na null da osveži stanje
    setSelectedStudentId(null);
    setSelectedStudent(null);
    setTimeout(() => {
      setSelectedStudentId(student.id);
      setSelectedStudent(student);
      setSchool(student.schoolid);
    }, 0);
  };

  return (
    <Grid container spacing={2} style={{ padding: '10px', height: '60vh' }}>
      <Grid item xs={3}>
        <Paper
          elevation={4}
          style={{
            padding: '20px',
            borderRadius: '16px',
            backgroundColor: '#f7f9fc',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            height: '50%'
          }}
        >
          <TextField
            fullWidth
            label={t('review.searchStudents')}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: '20px' }}
          />
          <Box sx={{ height: 'calc(100% - 60px)', overflowY: 'auto' }}>
            {loading ? (
              <CircularProgress />
            ) : (
              <List
                height={250}
                itemCount={filteredStudents.length}
                itemSize={50}
                width="100%"
              >
                {({ index, style }) => (
                  <ListItem
                    style={{
                      ...style,
                      backgroundColor:
                        filteredStudents[index].id === selectedStudentId
                          ? '#1976D2'
                          : '#f0f0f0',
                      color:
                        filteredStudents[index].id === selectedStudentId
                          ? '#fff'
                          : '#000',
                      borderRadius: '8px',
                      margin: '5px 0',
                      transition: 'background-color 0.3s ease'
                    }}
                    button
                    key={filteredStudents[index].id}
                    onClick={() => handleStudentClick(filteredStudents[index])}
                  >
                    <ListItemText
                      primary={filteredStudents[index].name}
                      style={{ paddingLeft: '10px' }}
                    />
                  </ListItem>
                )}
              </List>
            )}
          </Box>
        </Paper>

        <Paper
          elevation={4}
          style={{
            padding: '20px',
            borderRadius: '12px',
            backgroundColor: '#f0f4f8',
            marginTop: '20px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
          }}
        >
          {selectedStudent && (
            <Box
              style={{
                backgroundColor: '#ffffff',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
                width: '100%'
              }}
            >
              <Typography
                variant="h5"
                style={{
                  color: '#1976D2',
                  fontWeight: 'bold',
                  marginBottom: '12px'
                }}
              >
                {selectedStudent.name} - {selectedStudent.class}.
                {t('review.grade')}
              </Typography>

              <Typography style={{ marginBottom: '10px', color: '#455a64' }}>
                {t('review.gender')}:
                {selectedStudent.pol === 'm'
                  ? t('review.male')
                  : t('review.female')}
                | {t('review.language')}:
                {selectedStudent.language === 'Х'
                  ? t('review.croatian')
                  : t('review.serbian')}
              </Typography>

              <Typography style={{ marginBottom: '20px', color: '#455a64' }}>
                {t('review.totalPoints')}:
                <strong>{selectedStudent.point_sum}</strong>
              </Typography>

              <Typography
                variant="h6"
                style={{
                  color: '#1976D2',
                  marginBottom: '10px',
                  fontWeight: 'bold'
                }}
              >
                {t('review.school')}: {selectedStudent.school_name}
              </Typography>

              <Typography style={{ color: '#455a64', marginBottom: '10px' }}>
                {t('review.address')}: {selectedStudent.street},
                {selectedStudent.location_name} {selectedStudent.zipcode}
              </Typography>

              <Typography style={{ color: '#455a64', marginBottom: '20px' }}>
                {t('review.professor')}: {selectedStudent.professor}
              </Typography>

              <Box
                display="flex"
                justifyContent="flex-start"
                // gap={2}
                marginTop="20px"
              >
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: '#1976D2',
                    color: '#ffffff',
                    borderRadius: '8px',
                    padding: '6px 16px',
                    fontSize: '14px',
                    textTransform: 'none'
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = '#1565c0')
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = '#1976D2')
                  }
                  onClick={() =>
                    handleDialogToggle('student', true, selectedStudent)
                  }
                >
                  {t('review.changeStudentData')}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>

      <Grid item xs={4}>
        <Paper elevation={4} style={{ padding: '20px' }}>
          {selectedStudentId && (
            <StudentDetails
              studentId={selectedStudentId}
              onUpdate={fetchStudents}
            />
          )}
        </Paper>
      </Grid>

      <Grid item xs={5} style={{ height: '100%' }}>
        <Paper
          elevation={4}
          style={{
            padding: '20px',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}
        >
          {selectedStudentId && (
            <img
              src={`${API_BASE_URL}/api/radovi/finalni_radovi/${selectedStudent.class}/${
                selectedStudent.image_url ? selectedStudent.image_url : ''
              }`}
              alt={t('review.studentWork')}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '90%',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                marginBottom: '10px'
              }}
            />
          )}
        </Paper>
      </Grid>
      <AddStudentDialog
        key={
          selectedStudent ? selectedStudent.id + new Date().getTime() : 'new'
        }
        open={openStudentDialog}
        handleClose={() =>
          handleDialogToggle('student', false, selectedStudent)
        }
        studentData={selectedStudent}
        onSuccess={fetchStudents}
      />
    </Grid>
  );
};

export default FinalClassStudents;
